import { createElement } from 'react';
import { defineModule } from '../../engine/module';
import { QuoteBuilderPage } from './QuoteBuilderPage';
import { TemplateBlueprintTab } from './TemplateBlueprintTab';
import { ExpiryCell, OneTimeTotalCell, QuoteRefCell, RecurringTotalCell } from './cells';
import './quoting.css';

// Quoting module UI (docs/modules/quoting.md): the "Sales" nav — quotes (grid + board by
// status, the builder), the product catalog (list/detail with bundle + price-book tabs),
// price books, templates (with the blueprint editor tab), reminder schedules. Everything
// but the builder is an engine screen. The public acceptance page lives in
// web/src/public/quote and is routed from App.tsx (/q/:token), outside the shell.

export const quotingModule = defineModule({
  name: 'quoting',
  nav: [
    {
      label: 'Sales',
      order: 15,
      items: [{ label: 'Quotes', to: '/quotes', icon: 'quote', key: 'q' }],
    },
    {
      label: 'Customers & sales',
      area: 'config',
      order: 20,
      items: [
        { label: 'Products', to: '/products', icon: 'package' },
        { label: 'Product categories', to: '/product-categories', icon: 'folder' },
        { label: 'Price books', to: '/price-books', icon: 'book-open' },
        { label: 'Quote templates', to: '/quote-templates', icon: 'file-text' },
        { label: 'Quote reminders', to: '/quote-reminder-configs', icon: 'bell' },
      ],
    },
  ],
  screens: [
    {
      path: '/quotes',
      resource: 'quotes',
      kind: 'list',
      title: 'Quotes',
      subtitle: 'Proposals from draft to accepted, then into invoices and contracts',
      columns: [
        { field: 'ref', label: 'Ref', width: 150, sortable: true, render: (row) => createElement(QuoteRefCell, { row }) },
        'title',
        'client_name',
        'status',
        { field: 'one_time_total', label: 'One-time', align: 'right', width: 130, render: (row) => createElement(OneTimeTotalCell, { row }) },
        { field: 'recurring_total', label: 'Recurring', align: 'right', width: 140, render: (row) => createElement(RecurringTotalCell, { row }) },
        { field: 'expires_at', label: 'Expires', width: 130, sortable: true, render: (row) => createElement(ExpiryCell, { row }) },
        'owner_tech_id',
      ],
      filters: [{ field: 'status', kind: 'chips' }],
      views: ['table', 'board'],
      board: { groupBy: 'status', cardFields: ['title', 'expires_at'] },
      form: { fields: ['client_id', 'contact_id', 'site_id', 'template_id', 'title', 'owner_tech_id'] },
      detailPath: '/quotes/:id',
    },
    { path: '/quotes/:id', resource: 'quotes', kind: 'custom', title: 'Quote', component: QuoteBuilderPage },

    {
      path: '/products',
      resource: 'products',
      kind: 'list',
      title: 'Products',
      subtitle: 'What you sell — one-off, recurring, labour and bundles',
      columns: ['sku', 'name', 'kind', 'category_id', 'price_minor', 'cost_minor', 'recurring_interval', 'is_active'],
      filters: [{ field: 'kind', kind: 'chips' }, { field: 'is_active', kind: 'toggle' }],
      query: { sort: 'name' },
    },
    {
      path: '/products/:id',
      resource: 'products',
      kind: 'detail',
      tabs: [
        { key: 'bundle', label: 'Bundle items', icon: 'layers', resource: 'product-bundle-items', filter: { bundle_product_id: ':id' }, columns: ['product_id', 'qty', 'sort_order'], when: (row) => row.kind === 'bundle' },
        { key: 'prices', label: 'Price book entries', icon: 'book-open', resource: 'price-book-entries', filter: { product_id: ':id' }, columns: ['price_book_id', 'price_minor', 'cost_minor'] },
      ],
    },
    { path: '/product-categories', resource: 'product-categories', kind: 'list', title: 'Product categories', columns: ['name', 'description', 'sort_order', 'is_active'], query: { sort: 'sort_order' } },

    {
      path: '/price-books',
      resource: 'price-books',
      kind: 'list',
      title: 'Price books',
      subtitle: 'Per-client and default price overrides',
      columns: ['name', 'client_id', 'currency', 'is_default', 'is_active'],
      filters: [{ field: 'is_active', kind: 'toggle' }],
    },
    {
      path: '/price-books/:id',
      resource: 'price-books',
      kind: 'detail',
      tabs: [{ key: 'entries', label: 'Entries', icon: 'tag', resource: 'price-book-entries', filter: { price_book_id: ':id' }, columns: ['product_id', 'price_minor', 'cost_minor'] }],
    },

    {
      path: '/quote-templates',
      resource: 'quote-templates',
      kind: 'list',
      title: 'Quote templates',
      subtitle: 'Blueprints a new quote starts from',
      columns: ['name', 'description', 'expiry_days', 'requires_countersign', 'is_default', 'is_active'],
    },
    {
      path: '/quote-templates/:id',
      resource: 'quote-templates',
      kind: 'detail',
      tabs: [{ key: 'blueprint', label: 'Sections blueprint', icon: 'layers', component: TemplateBlueprintTab }],
    },
    { path: '/quote-reminder-configs', resource: 'quote-reminder-configs', kind: 'list', title: 'Quote reminders', subtitle: 'When to nudge — days after sending, days before expiry', columns: ['quote_id', 'is_enabled', 'remind_after_days', 'remind_before_expiry_days'] },
  ],
  commands: [
    { id: 'new-quote', label: 'New quote…', kind: 'create', icon: 'quote', keywords: 'proposal quotation sell', shortcut: 'g q', run: ({ navigate }) => navigate('/quotes?new=1') },
    { id: 'new-product', label: 'New product…', kind: 'create', icon: 'package', keywords: 'catalog sku', run: ({ navigate }) => navigate('/products?new=1') },
    { id: 'product-categories', label: 'Product categories', kind: 'go to', icon: 'folder', run: ({ navigate }) => navigate('/product-categories') },
    { id: 'quote-reminders', label: 'Quote reminder schedule', kind: 'go to', icon: 'bell', run: ({ navigate }) => navigate('/quote-reminder-configs') },
  ],
});
